import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { NewBusinessModal } from "../components/NewBusinessModal.tsx";
import { api, type BusinessDTO } from "../lib/api.ts";

const TIER_LABELS: Record<string, { label: string; color: string }> = {
	"A0": { label: "Tier A0 — board", color: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
	"A1": { label: "Tier A1 — operating", color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
	"A2": { label: "Tier A2 — operating", color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
	"B": { label: "Tier B — operating", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
	"venture-bet": {
		label: "Venture bet",
		color: "bg-amber-500/15 text-amber-300 border-amber-500/30",
	},
	"out": { label: "Out of taxonomy", color: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};

function fmtMoney(k: number | null): string {
	if (k === null || k === 0) return "—";
	if (k >= 1000) return `${(k / 1000).toFixed(1)}M`;
	return `${k}K`;
}

function isStatusStale(at: string | null): boolean {
	if (!at) return false;
	const ageDays = (Date.now() - new Date(at).getTime()) / (1000 * 60 * 60 * 24);
	return ageDays > 30;
}

export function Businesses() {
	const [businesses, setBusinesses] = useState<BusinessDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showNew, setShowNew] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const res = await api.listBusinesses();
				setBusinesses(res.businesses);
			} catch (err) {
				setError((err as Error).message);
			}
		})();
	}, []);

	const missingStatusCount =
		businesses?.filter(
			(b) => b.tier !== "out" && !b.latest_status_summary,
		).length ?? 0;

	return (
		<div className="px-6 py-8">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-xl font-semibold">Businesses</h1>
					<p className="text-sm text-zinc-500 mt-1">
						{businesses === null
							? "Loading…"
							: `${businesses.length} businesses${missingStatusCount > 0 ? ` · ${missingStatusCount} without recorded status` : ""}`}
					</p>
				</div>
				<button
					type="button"
					onClick={() => setShowNew(true)}
					className="text-sm px-3 py-1.5 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-200 hover:bg-zinc-800/40"
				>
					+ New business
				</button>
			</div>

			{showNew && <NewBusinessModal onClose={() => setShowNew(false)} />}

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{businesses?.map((b) => (
					<BusinessCard key={b.id} b={b} />
				))}
			</div>
		</div>
	);
}

function BusinessCard({ b }: { b: BusinessDTO }) {
	const tier = TIER_LABELS[b.tier] ?? TIER_LABELS["out"]!;
	const stale = isStatusStale(b.latest_status_at);

	return (
		<Link
			to={`/businesses/${b.id}`}
			className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 flex flex-col hover:border-zinc-700 hover:bg-zinc-900/60 transition-colors"
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<div className="text-base font-semibold text-zinc-100">{b.name}</div>
					<div className="text-xs text-zinc-500 mt-0.5">{b.id}</div>
				</div>
				<span className={`text-xs px-2 py-0.5 rounded border ${tier.color} whitespace-nowrap`}>
					{tier.label}
				</span>
			</div>

			<div className="mt-3 grid grid-cols-3 gap-3 text-xs">
				<div>
					<div className="text-zinc-600">Revenue/y</div>
					<div className="text-zinc-200 font-medium">{fmtMoney(b.revenue_year_k)}</div>
				</div>
				<div>
					<div className="text-zinc-600">Dividends/y</div>
					<div className="text-zinc-200 font-medium">{fmtMoney(b.dividends_year_k)}</div>
				</div>
				<div>
					<div className="text-zinc-600">Margin</div>
					<div className="text-zinc-200 font-medium">
						{b.margin_pct !== null ? `${b.margin_pct}%` : "—"}
					</div>
				</div>
			</div>

			<div className="mt-3 text-xs">
				<div className="text-zinc-600">Stage</div>
				<div className="text-zinc-300">{b.stage}</div>
			</div>

			{b.main_risk && (
				<div className="mt-3 text-xs">
					<div className="text-zinc-600">Main risk</div>
					<div className="text-amber-300/90">{b.main_risk}</div>
				</div>
			)}

			<div className="mt-4 pt-3 border-t border-zinc-800 text-xs">
				<div className="flex items-baseline justify-between">
					<div className="text-zinc-600">Current status</div>
					{b.latest_status_at && (
						<div className={`text-zinc-500 ${stale ? "text-amber-400" : ""}`}>
							{new Date(b.latest_status_at).toLocaleDateString()}
							{stale ? " (stale)" : ""}
						</div>
					)}
				</div>
				{b.latest_status_summary ? (
					<div className="mt-1.5 space-y-1">
						<div className="text-zinc-200">{b.latest_status_summary}</div>
						<div className="flex flex-wrap gap-x-3 gap-y-0.5 text-zinc-500">
							{b.latest_status_gate && <span>gate: {b.latest_status_gate}</span>}
							{b.latest_status_runway_months !== null && (
								<span>runway: {b.latest_status_runway_months}m</span>
							)}
							{b.latest_status_next_decision_due && (
								<span>next due: {b.latest_status_next_decision_due}</span>
							)}
						</div>
					</div>
				) : (
					<div className="mt-1.5 text-zinc-500 italic">
						{b.tier === "out" ? "—" : "no status recorded yet"}
					</div>
				)}
			</div>

			{b.notes && <div className="mt-3 text-xs text-zinc-500 italic">{b.notes}</div>}
		</Link>
	);
}
